import type { IntrinsicSystemProps, MotionPropsFor } from '@hover/blueprint';
import { AnimatePresence } from 'framer-motion';
import React, { useCallback, useEffect } from 'react';

import { useHomeownerTranslate } from 'src/modules/homeownerApp/hooks';
import { useImperativeTracking } from 'src/modules/homeownerApp/hooks/useImperativeTracking';
import { SkipSSR } from 'src/shared/components/SSR/SkipSSR';
import { fadePresence, presenceVariants } from 'src/shared/utils/motion';

import type {
  HandlePlacementMode,
  Segment,
  SegmentationOverlayProps,
} from '../SegmentationOverlay';
import {
  SegmentationOverlay,
  instancesToMaterials,
} from '../SegmentationOverlay';
import {
  useCustomizationSegmentHandleFunctionsRef,
  useCustomizationSegmentation,
} from '../useCustomization';
import { useIsSubregionSelectionEnabled } from '../useIsSubregionSelectionEnabled';

type MotionDivProps = MotionPropsFor<IntrinsicSystemProps<'div'>>;
export interface CustomizeSegmentationProps
  extends Partial<SegmentationOverlayProps> {
  isVisible?: boolean;
}

const variants = presenceVariants({
  animate: { opacity: 1, transition: { duration: 0.25 } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
  initial: { opacity: 0 },
});

const useSubregionMaterialImpression = ({
  segments,
  handlePlacementMode,
  renderSegmentationOverlay,
}: {
  segments: Segment[];
  handlePlacementMode: HandlePlacementMode;
  renderSegmentationOverlay: boolean;
}) => {
  const { trackingRef } = useImperativeTracking();

  useEffect(() => {
    if (!renderSegmentationOverlay || handlePlacementMode !== 'material') {
      return;
    }

    trackingRef.current?.buttonImpression({
      button_text: 'dot',
      primary_cta: false,
      total_material_classes: instancesToMaterials(segments).length,
      track_id: 'sub-region-dot',
    });
  }, [segments, handlePlacementMode, renderSegmentationOverlay, trackingRef]);
};

const useTooltipModuleViewed = ({
  renderSegmentationOverlay,
  isTooltipOpen,
}: {
  renderSegmentationOverlay: boolean;
  isTooltipOpen: boolean;
}) => {
  const { trackingRef } = useImperativeTracking();

  useEffect(() => {
    if (!renderSegmentationOverlay || !isTooltipOpen) {
      return;
    }

    trackingRef.current?.moduleViewed({
      track_id: 'sub-region-tool-tip',
    });
  }, [isTooltipOpen, renderSegmentationOverlay, trackingRef]);
};

export const CustomizeSegmentation = ({
  isVisible = true,
  highlightsProps,
  handlesProps,
  ...props
}: CustomizeSegmentationProps) => {
  const t = useHomeownerTranslate();
  const {
    data,
    surface,
    handlePlacementMode,
    isTooltipOpen,
    setSelection,
    selection,
    selectedSegments,
    hasSegmentation,
  } = useCustomizationSegmentation();
  const { trackingRef } = useImperativeTracking();

  const isSubregionSelectionEnabled = useIsSubregionSelectionEnabled();
  const renderSegmentationOverlay =
    !!data && !!surface && hasSegmentation && isSubregionSelectionEnabled;

  useSubregionMaterialImpression({
    segments: data?.segments || [],
    handlePlacementMode,
    renderSegmentationOverlay,
  });

  useTooltipModuleViewed({
    isTooltipOpen,
    renderSegmentationOverlay,
  });

  const setSelectionWithTracking = useCallback(
    (value: string[]) => {
      const materials = instancesToMaterials(data?.segments ?? []);
      trackingRef.current?.buttonPressed({
        button_text: 'dot',
        primary_cta: false,
        track_id: 'sub-region-dot',
        total_material_classes: materials.length,
      });
      setSelection(value);
    },
    [setSelection, trackingRef, data?.segments],
  );

  const handleFunctionsRef = useCustomizationSegmentHandleFunctionsRef();

  const visibility = {
    animate: isVisible ? 'animate' : 'exit',
    ...(isVisible ? {} : { pointerEvents: 'none' }),
  } satisfies MotionDivProps;

  return (
    <SkipSSR>
      <AnimatePresence>
        {renderSegmentationOverlay && (
          <SegmentationOverlay
            data={data}
            handleFunctionsRef={handleFunctionsRef}
            handlePlacementMode={handlePlacementMode}
            handlesProps={{ ...variants, ...visibility, ...handlesProps }}
            highlightsProps={{
              ...variants,
              ...visibility,
              ...highlightsProps,
            }}
            isTooltipOpen={isTooltipOpen}
            isVisible={isVisible}
            onSelectedHandlesChange={setSelectionWithTracking}
            selectedHandles={selection}
            selectedSegments={
              selectedSegments?.map(segment => segment.id) ?? []
            }
            tooltip={t('customize.subRegionTooltip')}
            {...fadePresence}
            {...props}
          />
        )}
      </AnimatePresence>
    </SkipSSR>
  );
};
